/**
 * IntegrationRegistry - Singleton Pattern
 *
 * Centralized registry for all integration definitions with O(1) lookups.
 * Provides type-safe access to integration metadata and category organization.
 *
 * Performance characteristics:
 * - O(1) lookup by integration ID
 * - O(1) lookup by category (via Map)
 * - O(n) for search operations (where n = total integrations)
 *
 * Usage:
 * ```typescript
 * const registry = IntegrationRegistry.getInstance();
 * const jira = registry.getIntegration(Integration.jira);
 * const cloudIntegrations = registry.getCategoryIntegrations(IntegrationCategory.CloudServiceProviders);
 * ```
 */

import { IntegrationCategory } from '@/features/integrations/types/category.types';
import type { IntegrationDefinition } from '@/features/integrations/types/integration.types';
import { Integration } from '@/types';

export class IntegrationRegistry {
  private static instance: IntegrationRegistry;

  /**
   * Map for O(1) integration lookups by ID
   */
  private integrations: Map<Integration, IntegrationDefinition> = new Map();

  /**
   * Map for O(1) category lookups
   */
  private categories: Map<IntegrationCategory, Integration[]> = new Map();

  /**
   * Private constructor enforces singleton pattern
   */
  private constructor() {
    // Initialize empty maps - will be populated via register() calls
  }

  /**
   * Get singleton instance of registry
   */
  public static getInstance(): IntegrationRegistry {
    if (!IntegrationRegistry.instance) {
      IntegrationRegistry.instance = new IntegrationRegistry();
    }
    return IntegrationRegistry.instance;
  }

  /**
   * Register a single integration definition
   * @throws Error if integration ID already registered
   */
  public register(
    integration: IntegrationDefinition,
    category: IntegrationCategory
  ): void {
    if (this.integrations.has(integration.id)) {
      throw new Error(
        `Integration "${integration.id}" is already registered. Duplicate registration attempted.`
      );
    }

    // Add to integrations map
    this.integrations.set(integration.id, integration);

    // Add to category map
    const existingCategory = this.categories.get(category) || [];
    this.categories.set(category, [...existingCategory, integration.id]);
  }

  /**
   * Register multiple integration definitions at once
   * More efficient than calling register() multiple times
   */
  public registerAll(
    integrations: Array<{
      definition: IntegrationDefinition;
      category: IntegrationCategory;
    }>
  ): void {
    integrations.forEach(({ definition, category }) => {
      this.register(definition, category);
    });
  }

  /**
   * Get integration definition by ID
   * O(1) time complexity
   *
   * @param id - Integration identifier
   * @returns Integration definition or undefined if not found
   */
  public getIntegration(id: Integration): IntegrationDefinition | undefined {
    return this.integrations.get(id);
  }

  /**
   * Get all integrations
   *
   * @returns Array of all registered integration definitions
   */
  public getAllIntegrations(): IntegrationDefinition[] {
    return Array.from(this.integrations.values());
  }

  /**
   * Get all integrations in a specific category
   * O(1) time complexity for category lookup, O(n) for mapping to definitions
   *
   * @param category - Integration category
   * @returns Array of integration definitions in the category
   */
  public getCategoryIntegrations(
    category: IntegrationCategory
  ): IntegrationDefinition[] {
    const integrationIds = this.categories.get(category) || [];
    return integrationIds
      .map(id => this.integrations.get(id))
      .filter((def): def is IntegrationDefinition => def !== undefined);
  }

  /**
   * Get all cloud provider integrations (AWS, Azure, GCP)
   * Convenience method for common cloud provider filtering
   *
   * @returns Array of cloud provider integration definitions
   */
  public getCloudProviders(): IntegrationDefinition[] {
    return this.getCategoryIntegrations(
      IntegrationCategory.CloudServiceProviders
    );
  }

  /**
   * Get all categories
   *
   * @returns Array of all integration categories
   */
  public getAllCategories(): IntegrationCategory[] {
    return Array.from(this.categories.keys());
  }

  /**
   * Get category for a specific integration
   *
   * @param id - Integration identifier
   * @returns Category or undefined if integration not found
   */
  public getIntegrationCategory(
    id: Integration
  ): IntegrationCategory | undefined {
    for (const [category, integrations] of this.categories.entries()) {
      if (integrations.includes(id)) {
        return category;
      }
    }
    return undefined;
  }

  /**
   * Search integrations by name or description
   * O(n) time complexity where n = total integrations
   *
   * @param query - Search query (case-insensitive)
   * @returns Array of matching integration definitions
   */
  public search(query: string): IntegrationDefinition[] {
    const lowerQuery = query.toLowerCase();
    return this.getAllIntegrations().filter(integration => {
      const nameMatch = integration.name.toLowerCase().includes(lowerQuery);
      const descriptionMatch =
        typeof integration.description === 'string' &&
        integration.description.toLowerCase().includes(lowerQuery);
      return nameMatch || descriptionMatch;
    });
  }

  /**
   * Check if integration exists
   *
   * @param id - Integration identifier
   * @returns true if integration is registered
   */
  public has(id: Integration): boolean {
    return this.integrations.has(id);
  }

  /**
   * Get count of registered integrations
   *
   * @returns Total number of registered integrations
   */
  public count(): number {
    return this.integrations.size;
  }

  /**
   * Get count of integrations per category
   *
   * @returns Map of category to integration count
   */
  public getCategoryCounts(): Map<IntegrationCategory, number> {
    const counts = new Map<IntegrationCategory, number>();
    for (const [category, integrations] of this.categories.entries()) {
      counts.set(category, integrations.length);
    }
    return counts;
  }

  /**
   * Validate registry state
   * Useful for debugging and testing
   *
   * @returns Validation result with errors if any
   */
  public validate(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check for integrations without categories
    const allIntegrationIds = Array.from(this.integrations.keys());
    const categorizedIds = new Set(Array.from(this.categories.values()).flat());

    allIntegrationIds.forEach(id => {
      if (!categorizedIds.has(id)) {
        errors.push(`Integration "${id}" is not assigned to any category`);
      }
    });

    // Check for category inconsistencies
    for (const [category, ids] of this.categories.entries()) {
      ids.forEach(id => {
        if (!this.integrations.has(id)) {
          errors.push(
            `Category "${category}" references non-existent integration "${id}"`
          );
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Clear all registrations (primarily for testing)
   * ⚠️ Use with caution - will remove all registered integrations
   */
  public clear(): void {
    this.integrations.clear();
    this.categories.clear();
  }

  /**
   * Reset singleton instance (primarily for testing)
   * ⚠️ Use with caution - forces new instance creation
   */
  public static reset(): void {
    if (IntegrationRegistry.instance) {
      IntegrationRegistry.instance.clear();
    }
    IntegrationRegistry.instance = new IntegrationRegistry();
  }
}

/**
 * Convenience function to get registry instance
 */
export const getIntegrationRegistry = () => IntegrationRegistry.getInstance();
